<script setup lang="ts">
import type { MenuItem } from '@/config/menu';
import { isMenuItemNew, menuGroupKey } from '@/config/menu';
import { useI18n } from 'vue-i18n';
import { computed, onMounted } from 'vue';
import DropDown from '../DropDown/index.vue';
import { Icon } from '@iconify/vue';
import { useTicketBadgeStore } from '@/stores/ticketBadgeStore';
import { usePurchaseOrderBadgeStore } from '@/stores/purchaseOrderBadgeStore';
import { useReembolsoBadgeStore } from '@/stores/reembolsoBadgeStore';
import { useAuthStore } from '@/stores/authStore';

const props = defineProps<{ item?: MenuItem; level?: number; parentKey?: string }>();
const { t } = useI18n();
const auth = useAuthStore();

// Chave hierárquica única do grupo (evita colisão de títulos iguais entre módulos).
const groupKey = computed(() => menuGroupKey(props.item?.title ?? '', props.parentKey));

// Badge de notificação (chamados novos / pedidos aguardando aprovação) — inicia o poller.
const ticketBadge = useTicketBadgeStore();
const purchaseOrderBadge = usePurchaseOrderBadgeStore();
const reembolsoBadge = useReembolsoBadgeStore();

/**
 * `reembolsoAprovacao`/`reembolsoConferencia` ficam marcados nos itens FOLHA do menu
 * (ex.: "Minhas solicitações", "Conferência" — renderizados por <DropDown>, sem onMounted
 * próprio), não no grupo pai ("Reembolso"/"Financeiro") que este NavCollapse desenha. Por
 * isso o start()/chip precisa olhar recursivamente para `children` e "borbulhar" para os
 * grupos ancestrais, em vez de checar só `item.badgeKey` diretamente.
 */
function subtreeHasBadgeKey(item: MenuItem | undefined, key: NonNullable<MenuItem['badgeKey']>): boolean {
  if (!item) return false;
  if (item.badgeKey === key) return true;
  return !!item.children?.some((child) => subtreeHasBadgeKey(child, key));
}

onMounted(() => {
  if (props.item?.badgeKey === 'ticketNew') ticketBadge.start();
  if (props.item?.badgeKey === 'purchaseOrderNew') purchaseOrderBadge.start();
  if (
    subtreeHasBadgeKey(props.item, 'reembolsoAprovacao') ||
    subtreeHasBadgeKey(props.item, 'reembolsoConferencia')
  ) {
    reembolsoBadge.start();
  }
});

/** Evita `onClick` duplicado no `v-list-item` (Vuetify não encadeia o handler do ativador no clique da linha). */
function activatorBindWithoutClick(activatorProps: Record<string, unknown>) {
    const { onClick: _omit, ...rest } = activatorProps;
    return rest;
}

function onActivatorClick(
    e: MouseEvent | KeyboardEvent,
    activatorProps: { onClick?: (ev: MouseEvent) => void }
) {
    activatorProps.onClick?.(e as MouseEvent);
}

// Há tags à direita (flag "New" e/ou sino)? Então o nome não deve encolher/truncar —
// ele fica no tamanho natural e as tags vão para a direita, junto do chevron.
const hasTitleTags = computed(() => {
  const it = props.item;
  if (!it) return false;
  return (
    isMenuItemNew(it, auth.user?.first_login_at) ||
    (it.badgeKey === 'ticketNew' && ticketBadge.count > 0) ||
    (it.badgeKey === 'purchaseOrderNew' && purchaseOrderBadge.count > 0) ||
    (subtreeHasBadgeKey(it, 'reembolsoAprovacao') && reembolsoBadge.aprovacoes > 0) ||
    (subtreeHasBadgeKey(it, 'reembolsoConferencia') && reembolsoBadge.conferencias > 0)
  );
});

const resolvedLevel = () => props.level ?? 0;
const iconDisplay = (icon?: string) =>
  icon && icon.includes(':') ? icon : 'solar:' + (icon ?? '');
const translatedTitle = (title?: string) => (title ? t(title) : '');
</script>

<template>
    <!-- ---------------------------------------------- -->
    <!---Item Childern -->
    <!-- ---------------------------------------------- -->
    <div v-if="item" class="mb-1">
        <v-list-group :value="groupKey">
            <!-- ---------------------------------------------- -->
            <!---Dropdown  -->
            <!-- ---------------------------------------------- -->
            <template v-slot:activator="{ props: activatorProps }">
                <v-list-item
                    v-bind="activatorBindWithoutClick(activatorProps)"
                    :value="groupKey"
                    :ripple="false"
                    :class="' bg-hover-' + item.BgColor"
                    :color="item.BgColor"
                    @click="onActivatorClick($event, activatorProps)"
                >
                    <!---Icon  -->
                    <template v-slot:prepend>
                        <div :class="'navbox  bg-hover-' + (item.BgColor ?? '')" :color="(item.BgColor as string | undefined)">
                            <span class="icon-box" v-if="resolvedLevel() > 0">
                                <div class="sublink-dot" width="30"></div>
                            </span>
                            <span class="icon-box" v-else>
                                <Icon
                                    :icon="iconDisplay(item.icon)"
                                    height="24"
                                    width="24"
                                    :level="level"
                                    :class="'position-relative z-index-2 texthover-' + item.BgColor"
                                />
                            </span>
                        </div>
                    </template>
                    <!---Title  -->
                    <v-list-item-title class="text-subtitle-1 font-weight-medium d-flex align-center" style="gap:6px;width:100%">
                        <span class="text-truncate desk-nav-title" :class="{ 'has-tags': hasTitleTags }">{{ translatedTitle(item.title) }}</span>
                        <!-- Cluster à direita (junto do chevron do acordeon): flag "New" + sino -->
                        <span class="desk-nav-tags">
                            <span v-if="isMenuItemNew(item, auth.user?.first_login_at)" class="menu-new-flag" title="Módulo novo">New</span>
                            <span
                                v-if="item.badgeKey === 'ticketNew' && ticketBadge.count > 0"
                                class="desk-nav-bell"
                                :title="ticketBadge.count + ' chamado(s) novo(s)'"
                            >
                                <Icon icon="solar:bell-bing-bold" :height="17" :width="17" class="desk-nav-bell__icon" />
                                <span class="desk-nav-bell__num">{{ ticketBadge.count > 99 ? '99+' : ticketBadge.count }}</span>
                            </span>
                            <span
                                v-if="item.badgeKey === 'purchaseOrderNew' && purchaseOrderBadge.count > 0"
                                class="desk-nav-bell"
                                :title="purchaseOrderBadge.count + ' pedido(s) aguardando sua aprovação'"
                            >
                                <Icon icon="solar:bell-bing-bold" :height="17" :width="17" class="desk-nav-bell__icon" />
                                <span class="desk-nav-bell__num">{{ purchaseOrderBadge.count > 99 ? '99+' : purchaseOrderBadge.count }}</span>
                            </span>
                            <span
                                v-if="subtreeHasBadgeKey(item, 'reembolsoAprovacao') && reembolsoBadge.aprovacoes > 0"
                                class="desk-nav-bell"
                                :title="reembolsoBadge.aprovacoes + ' reembolso(s) aguardando sua aprovação'"
                            >
                                <Icon icon="solar:bell-bing-bold" :height="17" :width="17" class="desk-nav-bell__icon" />
                                <span class="desk-nav-bell__num">{{ reembolsoBadge.aprovacoes > 99 ? '99+' : reembolsoBadge.aprovacoes }}</span>
                            </span>
                            <span
                                v-if="subtreeHasBadgeKey(item, 'reembolsoConferencia') && reembolsoBadge.conferencias > 0"
                                class="desk-nav-bell"
                                :title="reembolsoBadge.conferencias + ' reembolso(s) aguardando conferência'"
                            >
                                <Icon icon="solar:bell-bing-bold" :height="17" :width="17" class="desk-nav-bell__icon" />
                                <span class="desk-nav-bell__num">{{ reembolsoBadge.conferencias > 99 ? '99+' : reembolsoBadge.conferencias }}</span>
                            </span>
                        </span>
                    </v-list-item-title>
                    <!---If Caption-->
                    <v-list-item-subtitle v-if="item.subCaption" class="text-caption mt-n1 hide-menu">
                        {{ item.subCaption }}
                    </v-list-item-subtitle>
                </v-list-item>
            </template>
            <!-- ---------------------------------------------- -->
            <!---Sub Item-->
            <!-- ---------------------------------------------- -->
            <!-- Grupos aninhados (nível 1+) usam margem menor: o mb-4 do nível
                 raiz criaria vãos duplicados dentro do grupo pai. -->
            <div class="sublinks" :class="resolvedLevel() > 0 ? 'mb-1' : 'mb-4'">
                <template v-for="(subitem, i) in item.children" :key="i" v-if="item.children">
                    <NavCollapse :item="subitem" v-if="subitem.children" :level="resolvedLevel() + 1" :parent-key="groupKey" />
                    <DropDown :item="subitem" :level="resolvedLevel() + 1" v-else></DropDown>
                </template>
            </div>
        </v-list-group>
    </div>
    <!-- ---------------------------------------------- -->
    <!---End Item Sub Header -->
    <!-- ---------------------------------------------- -->
</template>

<style scoped>
/* Badge monocromático: só o sininho (vetor, herda a cor do menu) + nº discreto,
   empurrado para a direita (margin-left:auto) — fica junto do colapse, longe do nome. */
/* Nome: por padrão trunca (nomes longos). Quando há tags, fica no tamanho natural
   (não encolhe) — assim "Ticket" aparece inteiro e as tags é que cedem, se faltar espaço. */
.desk-nav-title { flex: 1 1 auto; min-width: 0; }
.desk-nav-title.has-tags { flex: 0 0 auto; }
/* Cluster de tags empurrado para a direita, junto do chevron do acordeon; cede se faltar espaço. */
.desk-nav-tags { margin-left: auto; display: inline-flex; align-items: center; gap: 4px; flex: 0 1 auto; min-width: 0; overflow: hidden; }
/* Reduz o vão antes do chevron (append), colando as tags no botão do acordeon. */
:deep(.v-list-group__header .v-list-item__append) { margin-inline-start: 6px; }
.desk-nav-bell {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  flex-shrink: 0;
  color: #e53935; /* vermelho, monocromático (só ícone + nº, sem pill) */
}
.desk-nav-bell__icon {
  transform-origin: 50% 10%;
  animation: deskBellShake 1.8s ease-in-out infinite;
}
.desk-nav-bell__num {
  font-size: 0.66rem;
  font-weight: 700;
  line-height: 1;
}
/* Etiqueta "New" de módulo recém-lançado (some sozinha após newUntil). */
.menu-new-flag {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 0.55rem;
  font-weight: 800;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: #fff;
  background: #0a9981;
  border-radius: 999px;
  padding: 0 5px;
  line-height: 1.6;
}
@keyframes deskBellShake {
  0%, 55%, 100% { transform: rotate(0); }
  60% { transform: rotate(-16deg); }
  68% { transform: rotate(13deg); }
  76% { transform: rotate(-9deg); }
  84% { transform: rotate(6deg); }
  92% { transform: rotate(-3deg); }
}
@media (prefers-reduced-motion: reduce) {
  .desk-nav-bell__icon { animation: none; }
}
</style>
